import type { PlayerProfileDoc } from '@/features/player/components/types';
import { firebaseCloudFunctionService } from '@/libs/api';
import { buildUrl, usKidsGolfService } from '@/libs/api/instances/us-kids-golf-instance';
import { format } from 'date-fns';

export const PLAYER_ENDPOINTS = {
  LIST_PLAYERS: '/sortFilterSearchPlayers',
  CREATE_COACH_PLAYER: '/coaches/me/players',
  COACH_PLAYER: (playerId: string) => `/coaches/me/players/${playerId}`,
} as const;

type CreateCoachPlayerRequest = {
  id?: string;
  academy_id?: string;
  firstname: string;
  lastname: string;
  dob: string;
  gender?: string;
  height?: number | null;
  clubsneeded: boolean;
  dominanthand: boolean;
  address?: PlayerProfileDoc['address'];
  level: number;
  email?: string;
  parent_first?: string;
  parent_last?: string;
  phone?: string;
  alternate_phone?: string;
  avatar_url?: string;
  avatarUpdatedAt?: number;
};

type UpdateCoachPlayerRequest = Omit<CreateCoachPlayerRequest, 'id'> & {
  progress?: PlayerProfileDoc['progress'];
  notes?: PlayerProfileDoc['notes'];
  welcomeKit?: PlayerProfileDoc['welcomeKit'];
};

const toCreateCoachPlayerRequest = (player: PlayerProfileDoc): CreateCoachPlayerRequest => {
  if (!player.firstname || !player.lastname || !player.dob?.seconds) {
    throw new Error('Missing required coach player fields');
  }

  return {
    id: player.id,
    academy_id: player.academy_id ?? undefined,
    firstname: player.firstname,
    lastname: player.lastname,
    dob: format(new Date(player.dob.seconds * 1000), 'yyyy-MM-dd'),
    gender: player.gender ?? undefined,
    height: player.height,
    clubsneeded: Boolean(player.clubsneeded),
    dominanthand: Boolean(player.dominanthand),
    address: player.address,
    level: Number(player.level),
    email: player.email ?? undefined,
    parent_first: player.parent_first ?? undefined,
    parent_last: player.parent_last ?? undefined,
    phone: player.phone ?? undefined,
    alternate_phone: player.alternate_phone ?? undefined,
    avatar_url: player.avatar_url ?? undefined,
    avatarUpdatedAt: player.avatarUpdatedAt ?? undefined,
  };
};

const toUpdateCoachPlayerRequest = (
  data: Partial<PlayerProfileDoc>,
): Partial<UpdateCoachPlayerRequest> => {
  const request: Partial<UpdateCoachPlayerRequest> = {};

  if (data.academy_id != null) request.academy_id = data.academy_id;
  if (data.firstname != null) request.firstname = data.firstname;
  if (data.lastname != null) request.lastname = data.lastname;
  if (data.dob?.seconds) {
    request.dob = format(new Date(data.dob.seconds * 1000), 'yyyy-MM-dd');
  }
  if (data.gender != null) request.gender = data.gender;
  if (data.height !== undefined) request.height = data.height;
  if (data.clubsneeded != null) request.clubsneeded = data.clubsneeded;
  if (data.dominanthand != null) request.dominanthand = data.dominanthand;
  if (data.address !== undefined) request.address = data.address;
  if (data.level != null) request.level = Number(data.level);
  if (data.email != null) request.email = data.email;
  if (data.parent_first != null) request.parent_first = data.parent_first;
  if (data.parent_last != null) request.parent_last = data.parent_last;
  if (data.phone != null) request.phone = data.phone;
  if (data.alternate_phone != null) request.alternate_phone = data.alternate_phone;
  if (data.avatar_url != null) request.avatar_url = data.avatar_url;
  if (data.avatarUpdatedAt != null) request.avatarUpdatedAt = data.avatarUpdatedAt;
  if (data.progress !== undefined) request.progress = data.progress;
  if (data.notes !== undefined) request.notes = data.notes;
  if (data.welcomeKit !== undefined) request.welcomeKit = data.welcomeKit;

  return request;
};

export const PlayerServices = {
  sortFilterSearchPlayers: async (request: player.SortFilterSearchPlayersRequest) => {
    const response = await firebaseCloudFunctionService.get<player.PlayersLookupResponse>(
      PLAYER_ENDPOINTS.LIST_PLAYERS,
      request,
    );
    return response;
  },

  createCoachPlayer: async (player: PlayerProfileDoc) => {
    return await usKidsGolfService.post<PlayerProfileDoc, CreateCoachPlayerRequest>(
      buildUrl(PLAYER_ENDPOINTS.CREATE_COACH_PLAYER),
      toCreateCoachPlayerRequest(player),
    );
  },

  getCoachPlayer: async (playerId: string) => {
    return await usKidsGolfService.get<PlayerProfileDoc>(
      buildUrl(PLAYER_ENDPOINTS.COACH_PLAYER(playerId)),
    );
  },

  updateCoachPlayer: async (playerId: string, data: Partial<PlayerProfileDoc>) => {
    return await usKidsGolfService.patch<PlayerProfileDoc, Partial<UpdateCoachPlayerRequest>>(
      buildUrl(PLAYER_ENDPOINTS.COACH_PLAYER(playerId)),
      toUpdateCoachPlayerRequest(data),
    );
  },

  removeCoachPlayer: async (playerId: string) => {
    await usKidsGolfService.delete<void>(buildUrl(PLAYER_ENDPOINTS.COACH_PLAYER(playerId)));
  },
};
